import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, PenLine, Wand2 } from 'lucide-react';
import { clsx } from 'clsx';
import { readWorkbookJson, writeWorkbookJson, type WorkbookActivityCompletion } from './workbookPersist';

export type RewriteExampleBlock = { lines: string[] };

export type RewriteItemView =
  | { q_no: number; mode: 'stem_completion'; stem: string; completionLines: string[] }
  | { q_no: number; mode: 'merge_sentences'; task: string; sentenceA: string; sentenceB: string }
  | { q_no: number; mode: 'fragments_order'; prompt: string; fragments: string[] };

export interface RewriteCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  sourcePage?: number;
  example?: { heading?: string; lines: string[] } | null;
  exampleBlocks?: RewriteExampleBlock[] | null;
  items: RewriteItemView[];
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type PersistShape = { answers: Record<number, string>; step: number };

export const RewriteCard: React.FC<RewriteCardProps> = ({
  workbookLabel,
  title,
  instructions,
  sourcePage,
  example,
  exampleBlocks,
  items,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-rw-${persistenceId}` : '';

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const itemsStableKey = items.map((it) => `${it.q_no}:${it.mode}`).join('|');

  const initial = useMemo(() => {
    const m: Record<number, string> = {};
    for (const it of items) m[it.q_no] = '';
    return m;
  }, [items]);

  const [answers, setAnswers] = useState<Record<number, string>>(initial);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const list = itemsRef.current;
    const empty: Record<number, string> = {};
    for (const it of list) empty[it.q_no] = '';

    if (!storageKey) {
      setAnswers(empty);
      setStep(0);
      return;
    }
    const loaded = readWorkbookJson<PersistShape | null>(storageKey, null);
    if (loaded?.answers && typeof loaded.answers === 'object') {
      const merged = { ...empty };
      const ra = loaded.answers as Record<string, unknown>;
      for (const k of Object.keys(ra)) {
        const q = Number(k);
        if (!Number.isFinite(q)) continue;
        merged[q] = String(ra[k] ?? '');
      }
      setAnswers(merged);
    } else setAnswers(empty);
    const max = Math.max(0, list.length - 1);
    const s = typeof loaded?.step === 'number' ? Math.min(Math.max(0, loaded.step), max) : 0;
    setStep(s);
  }, [storageKey, itemsStableKey, items.length]);

  useEffect(() => {
    if (!storageKey) return;
    writeWorkbookJson(storageKey, { answers, step } satisfies PersistShape);
  }, [storageKey, answers, step]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  useEffect(() => {
    if (!onWorkbookStatusChange || items.length === 0) return;
    const all = items.every((row) => (answers[row.q_no] ?? '').trim().length > 0);
    const any = items.some((row) => (answers[row.q_no] ?? '').trim().length > 0);
    onWorkbookStatusChange(all ? 'completed' : any ? 'in_progress' : 'not_started');
  }, [onWorkbookStatusChange, items, answers]);

  const it = items[Math.min(step, Math.max(0, items.length - 1))];

  const resetCurrent = () => {
    if (!it) return;
    mark();
    setAnswers((p) => ({ ...p, [it.q_no]: '' }));
  };

  const resetAll = () => {
    mark();
    setAnswers({ ...initial });
    setStep(0);
    if (storageKey) writeWorkbookJson(storageKey, { answers: initial, step: 0 });
  };

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-rose-300/90 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 shadow-[0_20px_50px_-12px_rgba(244,63,94,0.35)] dark:border-rose-800/60 dark:from-rose-950/40 dark:via-slate-900 dark:to-slate-900">
      <div className="border-b border-rose-200/70 bg-white/55 px-5 py-4 backdrop-blur-sm dark:border-rose-800/40 dark:bg-slate-900/50">
        {workbookLabel ? (
          <p className="text-sm font-extrabold text-rose-800 tamil-text dark:text-rose-200" lang="ta">
            {workbookLabel}
          </p>
        ) : null}
        <h2 className="mt-1 flex items-center gap-2 text-2xl font-extrabold text-rose-950 tamil-text md:text-3xl dark:text-rose-50" lang="ta">
          <Wand2 className="h-8 w-8 shrink-0 text-rose-500" aria-hidden />
          {title}
        </h2>
        {instructions ? (
          <p className="mt-2 text-lg leading-relaxed text-rose-900/95 tamil-text dark:text-rose-100/90" lang="ta">
            {instructions}
          </p>
        ) : null}
        {sourcePage != null ? (
          <span className="mt-3 inline-block rounded-full bg-rose-600 px-3 py-1 text-sm font-bold text-white">p.{sourcePage}</span>
        ) : null}
      </div>

      <div className="space-y-5 px-4 py-6 sm:px-8">
        {example && example.lines.length > 0 ? (
          <div className="rounded-3xl border-4 border-amber-200 bg-gradient-to-r from-amber-100 to-yellow-100 p-5 shadow-md dark:border-amber-800 dark:from-amber-950/50 dark:to-yellow-950/30">
            {example.heading ? (
              <p className="mb-2 text-lg font-extrabold text-amber-950 tamil-text dark:text-amber-100" lang="ta">
                {example.heading}
              </p>
            ) : null}
            <ul className="space-y-2 text-lg font-bold leading-relaxed text-amber-950 tamil-text dark:text-amber-50" lang="ta">
              {example.lines.map((line, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-amber-600">✦</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {exampleBlocks && exampleBlocks.length > 0 ? (
          <div className="grid gap-3">
            {exampleBlocks.map((block, bi) => (
              <div
                key={bi}
                className="rounded-2xl border-4 border-white bg-white/90 p-4 shadow-md dark:border-slate-600 dark:bg-slate-800/90"
              >
                <ul className="space-y-1 text-base font-semibold leading-relaxed text-rose-900 tamil-text dark:text-rose-50" lang="ta">
                  {block.lines.map((line, li) => (
                    <li key={li}>• {line}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}

        <p className="rounded-2xl bg-white/70 p-3 text-center text-base font-bold text-rose-800 tamil-text dark:bg-slate-800/80 dark:text-rose-200" lang="ta">
          எடுத்துக்காட்டைப் போல் முழு வாக்கியமாக எழுதுக ✏️
        </p>

        {it ? (
          <>
            <div className="flex items-center justify-center gap-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    mark();
                    setStep(i);
                  }}
                  className={clsx(
                    'h-3 w-3 rounded-full border-2 sm:h-4 sm:w-4',
                    i === step ? 'scale-125 border-rose-600 bg-rose-500' : 'border-rose-300 bg-white dark:border-rose-700 dark:bg-slate-700'
                  )}
                  aria-label={`படி ${i + 1}`}
                />
              ))}
            </div>

            <div className="rounded-3xl border-4 border-white bg-white/95 p-6 shadow-inner dark:border-slate-600 dark:bg-slate-800/95">
              <p className="text-center text-sm font-extrabold text-rose-700 tamil-text dark:text-rose-300" lang="ta">
                பயிற்சி {step + 1} / {items.length}
              </p>

              {it.mode === 'stem_completion' ? (
                <div className="mt-4 space-y-4">
                  <p className="text-center text-xl font-extrabold text-rose-950 tamil-text dark:text-rose-50" lang="ta">
                    {it.stem}
                  </p>
                  {it.completionLines.length > 0 ? (
                    <div className="flex flex-wrap justify-center gap-2">
                      {it.completionLines.map((c, ci) => (
                        <span
                          key={ci}
                          className="rounded-2xl bg-gradient-to-r from-fuchsia-400 to-pink-400 px-4 py-2 text-lg font-bold text-white shadow-md tamil-text"
                          lang="ta"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {it.mode === 'merge_sentences' ? (
                <div className="mt-4 space-y-4">
                  <p className="text-center text-lg font-extrabold text-rose-900 tamil-text dark:text-rose-100" lang="ta">
                    {it.task}
                  </p>
                  <div className="space-y-3 rounded-2xl border-4 border-rose-100 bg-rose-50/80 p-4 dark:border-rose-900 dark:bg-rose-950/30">
                    <p className="text-lg font-bold text-rose-950 tamil-text dark:text-rose-50" lang="ta">
                      {it.sentenceA}
                    </p>
                    <p className="text-lg font-bold text-rose-950 tamil-text dark:text-rose-50" lang="ta">
                      {it.sentenceB}
                    </p>
                  </div>
                </div>
              ) : null}

              {it.mode === 'fragments_order' ? (
                <div className="mt-4 space-y-4">
                  <p className="text-center text-lg font-bold text-rose-900 tamil-text dark:text-rose-100" lang="ta">
                    {it.prompt}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {it.fragments.map((f, fi) => (
                      <span
                        key={fi}
                        className="rounded-2xl border-4 border-app-primary/20 bg-app-primary/15 px-3 py-2 text-lg font-extrabold text-app-primary tamil-text dark:border-violet-700 dark:bg-app-primary/15/40 dark:text-violet-100"
                        lang="ta"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <label className="mt-6 flex items-center justify-center gap-2 text-lg font-extrabold text-rose-800 tamil-text dark:text-rose-200" lang="ta">
                <PenLine className="h-6 w-6" aria-hidden />
                உங்கள் வாக்கியம்
              </label>
              <textarea
                value={answers[it.q_no] ?? ''}
                onChange={(e) => {
                  mark();
                  setAnswers((p) => ({ ...p, [it.q_no]: e.target.value }));
                }}
                rows={6}
                className="mt-3 w-full min-h-[10rem] rounded-2xl border-4 border-rose-200 bg-rose-50/50 p-4 text-xl font-semibold leading-relaxed text-rose-950 outline-none focus:border-amber-400 dark:border-rose-800 dark:bg-slate-900 dark:text-white tamil-text"
                lang="ta"
                placeholder="இங்கே எழுதத் தொடங்குக…"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                disabled={step <= 0}
                onClick={() => {
                  mark();
                  setStep((s) => s - 1);
                }}
                className="inline-flex min-h-[52px] items-center gap-2 rounded-2xl border-4 border-rose-300 bg-white px-4 py-3 text-base font-extrabold text-rose-900 shadow-md disabled:opacity-40 dark:border-rose-700 dark:bg-slate-800 dark:text-rose-100 tamil-text"
                lang="ta"
              >
                <ChevronLeft className="h-6 w-6" />
                முந்தைய
              </button>
              <button
                type="button"
                onClick={resetCurrent}
                className="min-h-[52px] rounded-2xl border-4 border-amber-400 bg-amber-100 px-5 py-3 text-base font-extrabold text-amber-950 tamil-text dark:bg-amber-950/40 dark:text-amber-50"
                lang="ta"
              >
                மீண்டும் முயல்
              </button>
              <button
                type="button"
                disabled={step >= items.length - 1}
                onClick={() => {
                  mark();
                  setStep((s) => s + 1);
                }}
                className="inline-flex min-h-[52px] items-center gap-2 rounded-2xl border-4 border-rose-300 bg-white px-4 py-3 text-base font-extrabold text-rose-900 shadow-md disabled:opacity-40 dark:border-rose-700 dark:bg-slate-800 dark:text-rose-100 tamil-text"
                lang="ta"
              >
                அடுத்து
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>

            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={resetAll}
                className="text-sm font-bold text-rose-700 underline tamil-text dark:text-rose-300"
                lang="ta"
              >
                எல்லாப் பதில்களையும் அழி
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};
